import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { getChannelI18nKey, getOwnershipI18nKey } from '../TabHelper';

import { FilterProps } from './FilterHelper';
import { useLocation } from 'react-router';

export const RecommendedTabFilter: React.FC<FilterProps> = ({
  contacts,
  setFilteredContacts,
  sortDirection,
  sortColumn,
  sourcePage,
}: FilterProps): React.ReactElement => {
  const intl = useIntl();
  const optAll = 'ALL';
  const elementAll = (
    <SelectOption
      value={optAll}
      key={optAll}
      label={intl.formatMessage({
        id: 'cs_perfportal_all',
        defaultMessage: 'All',
      })}
    />
  );

  const optsCategory = new Array(
    ...new Set(contacts.map(c => c.category ?? ''))
  ).sort();
  const optsSic = new Array(...new Set(contacts.map(c => c.sic ?? ''))).sort();
  const optsChannel = new Array(
    ...new Set(contacts.map(c => c.channel ?? ''))
  ).sort();
  const optsOwner = new Array(
    ...new Set(contacts.map(c => c.ownership ?? ''))
  ).sort();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const categoryParam = params.get('category');
  const [selectedCategory, setSelectedCategory] = useState(
    categoryParam && optsCategory.includes(categoryParam)
      ? categoryParam
      : optAll
  );
  const [selectedSic, setSelectedSic] = useState(optAll);
  const [selectedChannel, setSelectedChannel] = useState(optAll);
  const [selectedOwner, setSelectedOwner] = useState(optAll);

  useEffect(() => {
    let filtered = new Array(...contacts);

    if (selectedCategory !== optAll) {
      filtered = filtered.filter(c => c.category === selectedCategory);
    }

    if (selectedSic !== optAll) {
      filtered = filtered.filter(c => c.sic === selectedSic);
    }

    if (selectedChannel !== optAll) {
      filtered = filtered.filter(c => c.channel === selectedChannel);
    }

    if (selectedOwner !== optAll) {
      filtered = filtered.filter(c => c.ownership === selectedOwner);
    }

    setFilteredContacts(filtered);
  }, [
    selectedCategory,
    selectedSic,
    selectedChannel,
    selectedOwner,
    sortDirection,
    sortColumn,
  ]);

  return (
    <Row width='100%' alignmentHorizontal='start'>
      <Text>
        <FormattedMessage
          id='cs_perfportal_filterBy'
          defaultMessage='Filter by'
        />
      </Text>

      <Select
        value={selectedCategory}
        onChange={setSelectedCategory}
        label={intl.formatMessage({
          id: 'cs_perfportal_category',
          defaultMessage: 'Category',
        })}
        width='200px'
        size='xlarge'
      >
        {elementAll}
        {optsCategory.map(opt => (
          <SelectOption
            label={opt}
            value={opt}
            key={opt}
            data-csa-c-tag1={sourcePage || ''}
            data-csa-c-tag2='Contact Table'
            data-csa-c-tag3='RECOMMENDED'
            data-csa-c-type='select'
            data-csa-c-slot-id='contact-table-cr-recommended-tab-category-filter-slot'
            data-csa-c-content-id='contact-table-cr-recommended-tab-category-filter-select'
            data-csa-c-selected-value={selectedCategory}
          />
        ))}
      </Select>

      <Select
        value={selectedSic}
        onChange={setSelectedSic}
        label={intl.formatMessage({
          id: 'cs_perfportal_contact_recom_sic',
          defaultMessage: 'SIC',
          description: 'SIC code for recommended contact',
        })}
        width='200px'
        size='xlarge'
      >
        {elementAll}
        {optsSic.map(opt => (
          <SelectOption
            label={opt}
            value={opt}
            key={opt}
            data-csa-c-tag1={sourcePage || ''}
            data-csa-c-tag2='Contact Table'
            data-csa-c-tag3='RECOMMENDED'
            data-csa-c-type='select'
            data-csa-c-slot-id='contact-table-cr-recommended-tab-sic-filter-slot'
            data-csa-c-content-id='contact-table-cr-recommended-tab-sic-filter-select'
            data-csa-c-selected-value={selectedSic}
          />
        ))}
      </Select>

      <Select
        value={selectedChannel}
        onChange={setSelectedChannel}
        label={intl.formatMessage({
          id: 'cs_perfportal_channel',
          defaultMessage: 'Channel',
        })}
        width='200px'
        size='xlarge'
      >
        {elementAll}
        {optsChannel.map(opt => (
          <SelectOption
            label={intl.formatMessage({
              id: getChannelI18nKey(opt),
              defaultMessage: opt,
            })}
            value={opt}
            key={opt}
            data-csa-c-tag1={sourcePage || ''}
            data-csa-c-tag2='Contact Table'
            data-csa-c-tag3='RECOMMENDED'
            data-csa-c-type='select'
            data-csa-c-slot-id='contact-table-cr-recommended-tab-channel-filter-slot'
            data-csa-c-content-id='contact-table-cr-recommended-tab-channel-filter-select'
            data-csa-c-selected-value={selectedChannel}
          />
        ))}
      </Select>

      <Select
        value={selectedOwner}
        onChange={setSelectedOwner}
        label={intl.formatMessage({
          id: 'cs_perfportal_ownership',
          defaultMessage: 'Ownership',
        })}
        width='200px'
        size='xlarge'
      >
        {elementAll}
        {optsOwner.map(opt => (
          <SelectOption
            label={intl.formatMessage({
              id: getOwnershipI18nKey(opt),
              defaultMessage: opt,
            })}
            value={opt}
            key={opt}
            data-csa-c-tag1={sourcePage || ''}
            data-csa-c-tag2='Contact Table'
            data-csa-c-tag3='RECOMMENDED'
            data-csa-c-type='select'
            data-csa-c-slot-id='contact-table-cr-recommended-tab-owner-filter-slot'
            data-csa-c-content-id='contact-table-cr-recommended-tab-owner-filter-select'
            data-csa-c-selected-value={selectedOwner}
          />
        ))}
      </Select>
    </Row>
  );
};
